// MobilePage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from '../../assets/images/branding-xs.png'

const MobilePage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        // Scroll animation observer
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const mobileStats = [
        { number: '70%+', label: 'of users interact with brands on mobile — yet most businesses underperform on mobile experiences' },
        { number: '53%', label: 'of users abandon sites that take longer than 3 seconds to load on mobile' },
        { number: '2x', label: 'higher conversion rates with optimised mobile experiences' },
        { number: '85%', label: 'of users expect mobile experiences to be as good as desktop' }
    ];

    const mobileFocus = [
        'Fast loading experiences',
        'Clean and intuitive navigation',
        'Thumb-friendly design',
        'Reduced drop-offs'
    ];

    const mobileServices = [
        {
            icon: 'fas fa-mobile-alt',
            title: 'Mobile UX Optimisation',
            desc: 'Improving how users interact with your mobile experience.',
            features: ['Mobile UX Audits', 'User Flow Optimisation', 'Navigation Improvements', 'Conversion Path Simplification']
        },
        {
            icon: 'fas fa-tachometer-alt',
            title: 'Mobile Performance Optimisation',
            desc: 'Speed is everything on mobile.',
            features: ['Load Speed Improvements', 'Image & Asset Optimisation', 'Core Web Vitals (Mobile)', 'Technical Performance Fixes']
        },
        {
            icon: 'fas fa-chart-line',
            title: 'App Experience Optimisation (ASO & UX)',
            desc: 'If you have an app, we help improve visibility and performance.',
            features: ['App Store Optimisation (ASO)', 'App UX Improvements', 'Retention & Engagement Optimisation', 'Funnel Analysis']
        },
        {
            icon: 'fas fa-chart-bar',
            title: 'Mobile CRO (Conversion Rate Optimisation)',
            desc: 'Turning mobile traffic into real results.',
            features: ['Mobile A/B Testing', 'Conversion Tracking Setup', 'Behaviour Analysis (heatmaps, sessions)', 'Checkout & Form Optimisation']
        }
    ];

    const benefits = [
        ['Mobile-first, not desktop-first', 'Experiences designed with mobile as the priority — not an afterthought'],
        ['Data-driven optimisation approach', 'Every decision backed by user behaviour data and performance analytics'],
        ['Focus on real business outcomes', 'Optimising for conversions and revenue, not vanity metrics'],
        ['Integrated with your overall marketing strategy', 'Seamless alignment with broader marketing and growth initiatives']
    ];

    const targetBusinesses = [
        'Businesses with underperforming mobile experiences seeking improved user engagement and conversion rates',
        'E-commerce brands needing optimised mobile checkout flows and product discovery experiences',
        'Companies with mobile apps requiring better visibility, user retention, and engagement metrics',
        'Marketing teams looking to maximise mobile traffic conversion through CRO and testing',
        'Organisations undergoing digital transformation with mobile-first initiatives'
    ];

    const mobileQuestions = [
        'How can your mobile experience become your strongest conversion channel?',
        'What UX improvements would have the greatest impact on mobile user satisfaction?',
        'How do you ensure fast, seamless experiences across all mobile devices?',
        'What app store optimisation strategies improve visibility and downloads?',
        'How can mobile CRO turn more traffic into real revenue?'
    ];

    const mobileTechniques = [
        'Mobile-First UX Design & User Flow Optimisation',
        'Mobile Performance Audits & Core Web Vitals',
        'App Store Optimisation (ASO) Strategy',
        'Mobile Conversion Rate Optimisation (CRO)',
        'Behaviour Analysis & Heatmap Testing',
        'Cross-Device Experience Consistency'
    ];

    const serviceComponents = [
        { component: 'Mobile UX Optimisation', impact: 'Improved usability, navigation, and user satisfaction on mobile devices' },
        { component: 'Mobile Performance Optimisation', impact: 'Faster load times, better Core Web Vitals, and reduced bounce rates' },
        { component: 'App Experience Optimisation', impact: 'Higher app store rankings, increased downloads, and improved retention' },
        { component: 'Mobile CRO', impact: 'More conversions from mobile traffic, higher revenue, and better ROI' }
    ];

    return (
        <div className="branding sv-page">
            {/* Decorative Image - Top of Page */}
            <div className="sv-decorative-image-container">

            </div>
            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            {/* Page Title Section */}
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">Mobile Experience & App Optimisation</span>
                    </h1>
                    <p>Optimised mobile experiences that turn users into customers.</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    {/* Hero Content Section */}
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    Over 70% of users interact with brands on mobile — yet most businesses underperform on mobile experiences.
                                </p>
                                <p className="sv-description">
                                    At Reachtix, we optimise and enhance mobile performance across websites and apps to deliver seamless, fast, and conversion-driven user experiences.
                                </p>
                                <p className="sv-description">
                                    We design and optimise with mobile as the priority — not an afterthought. Every interaction is built to be fast, intuitive, and conversion-focused.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Book a Free Consultation
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-search mr-2"></i>
                                        Get a Mobile Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Stats Section */}
                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            The <span className="sv-text-gradient">Mobile Experience Impact</span>
                        </h2>
                        <Row className="sv-stats-grid">
                            {mobileStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    {/* Mobile-First Strategy Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Mobile-First Strategy</h2>
                                <p className="sv-section-description">
                                    We design and optimise with mobile as the priority — not an afterthought.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Our focus:</h4>
                                    {mobileFocus.map((item, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-check-circle"></i>
                                            {item}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* What Is Mobile Optimisation Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What We Deliver</h2>
                                <p className="sv-section-description">
                                    We optimise and enhance mobile performance across websites and apps to deliver seamless, fast, and conversion-driven user experiences.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Mobile Questions We Address:</h4>
                                    {mobileQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Techniques Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Advanced Mobile Techniques</h2>
                                <p className="sv-section-description">
                                    We employ sophisticated mobile optimisation methodologies to create mobile experiences that are fast, intuitive, and conversion-focused across all devices and platforms.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Core Mobile Methodologies:</h4>
                                    {mobileTechniques.map((technique, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-cogs"></i>
                                            {technique}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Why Mobile Matters Section */}
                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Reachtix for Mobile?</h2>
                                <p className="sv-section-description">
                                    We don't just optimise mobile experiences — we transform them into your strongest conversion channel.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-mobile-alt"></i>
                                        <h5>Mobile-first, not desktop-first</h5>
                                        <p>Mobile is the priority — not an afterthought</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-line"></i>
                                        <h5>Data-driven optimisation</h5>
                                        <p>Every decision backed by user behaviour and performance data</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-bullseye"></i>
                                        <h5>Focus on real business outcomes</h5>
                                        <p>Optimising for conversions and revenue, not vanity metrics</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-bar"></i>
                                        <h5>Integrated with your marketing strategy</h5>
                                        <p>Seamless alignment with broader marketing and growth initiatives</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Our Mobile Services Section */}
                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our Mobile Services at Reachtix</h2>
                                <p className="sv-section-description">
                                    We offer comprehensive mobile solutions that combine UX design, performance optimisation, app store strategy, and conversion rate optimisation to deliver measurable business outcomes.
                                </p>

                                <div className="sv-approaches-grid">
                                    {mobileServices.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                                <ul className="sv-feature-list">
                                                    {item.features.map((feature, idx) => (
                                                        <li key={idx}><i className="fas fa-check-circle"></i> {feature}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Service Components Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Integrated Mobile Service Components</h2>
                                <p className="sv-section-description mb-4">
                                    Our holistic approach to mobile optimisation ensures every element works together to create seamless, high-converting mobile experiences that drive both user satisfaction and business results.
                                </p>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Service Component</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {serviceComponents.map((component, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{component.component}</strong>
                                                    </td>
                                                    <td>{component.impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Benefits Section */}
                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Benefits of Mobile Optimisation with Reachtix</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Strategic Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                <p className="mt-4 sv-section-description">
                                    Strategic mobile optimisation transforms your mobile presence from underperforming to exceptional, enabling superior user engagement, improved conversion rates, and sustainable mobile-first growth.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Who Should Invest Section */}
                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Who Benefits Most from Mobile Optimisation Services?</h2>
                                <p className="sv-section-description">
                                    Mobile optimisation services deliver maximum value for organisations seeking to improve mobile user experiences, increase conversion rates, and drive measurable business outcomes:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    Make your mobile experience your strongest channel.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    {/* Why Partner With Reachtix Section */}
                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">Why Choose Reachtix for Mobile Optimisation?</h2>
                                <p className="sv-section-description">
                                    At Reachtix, we believe exceptional mobile experiences are built at the intersection of UX design, performance optimisation, and data-driven strategy. Our approach combines mobile-first thinking, technical excellence, and measurable business outcomes.
                                </p>
                                <p className="sv-section-description">
                                    We don't just optimise mobile experiences — we transform them into powerful conversion channels. Our data-driven approach, continuous testing, and focus on real business outcomes ensure we deliver mobile experiences that users love and businesses rely on for growth.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Our Mobile Advantages:</h4>
                                    <div className="sv-question-item">
                                        <i className="fas fa-mobile-alt"></i>
                                        Mobile-first, not desktop-first
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-chart-line"></i>
                                        Data-driven optimisation approach
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-bullseye"></i>
                                        Focus on real business outcomes (not vanity metrics)
                                    </div>
                                    <div className="sv-question-item">
                                        <i className="fas fa-chart-bar"></i>
                                        Integrated with your overall marketing strategy
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    {/* Final CTA Section */}
                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Make your mobile experience your strongest channel.</h2>
                                <p className="sv-cta-description">
                                    Over 70% of users interact with brands on mobile — don't let poor mobile experiences cost you customers. From UX audits to performance optimisation and app store strategy, we help you create mobile experiences that convert.
                                </p>
                                <p className="sv-cta-description">
                                    At Reachtix, we treat mobile optimisation as a strategic business discipline — not just technical fixes. Our specialists blend UX expertise with commercial insight to make your mobile presence a powerful driver of user satisfaction, conversions, and sustainable growth.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='primary'
                                    >
                                        <i className="fas fa-mobile-alt mr-2"></i>
                                        Book a Free Consultation
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='light'
                                    >
                                        <i className="fas fa-search mr-2"></i>
                                        Get a Mobile Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default MobilePage;